/**
 * Faithful single-target damage formula (the real, documented mainline mechanics).
 *
 * base = floor(floor(floor(2*L/5 + 2) * power * A / D) / 50) + 2
 *
 * then, as integer-truncating steps in mainline order:
 *   targets (×0.75 if multi-target) → critical (×1.5) → random r∈85..100 (×r/100)
 *   → STAB (×1.5) → type effectiveness (product over defender types; 0 = immune)
 *   → burn (×0.5 for a physical move while the attacker is burned)
 *   → item/ability hook (otherMultiplier)
 *
 * A/D are taken from ComputedStats by move category (physical → atk/def, special →
 * spatk/spdef), with in-battle stat-stage multipliers applied. A CRITICAL hit ignores
 * the attacker's NEGATIVE and the defender's POSITIVE stage multipliers (you never lose
 * a crit's value to a debuff or the foe's buff).
 *
 * Minimum 1 damage when not immune; exactly 0 when immune (or for status/0-power moves).
 * Pure + deterministic: the random roll flows through the injected Rng.
 */
import type { BattleParticipant, MoveData, ComputedStats, PokemonType, StatKey } from '@/shared';
import type { Rng } from './rng.js';
import { effectivenessAgainst } from './typeChart.js';

/**
 * Context the engine assembles for one move use. The pure formula needs the
 * combatants' derived stats + types, which BattleParticipant doesn't carry, plus
 * optional modifier toggles. Kept out of the frozen @/shared shapes on purpose.
 */
export interface DamageContext {
  /** Attacker's full computed stats (post nature/IV/EV, pre stage). */
  readonly attackerStats: ComputedStats;
  /** Defender's full computed stats (post nature/IV/EV, pre stage). */
  readonly defenderStats: ComputedStats;
  /** Attacker's species types (for STAB). */
  readonly attackerTypes: readonly PokemonType[];
  /** Defender's species types (for effectiveness). */
  readonly defenderTypes: readonly PokemonType[];
  /** Number of targets the move hit; >1 applies the 0.75 spread modifier. */
  readonly targets?: number;
  /** Force this hit to be critical (otherwise non-crit). */
  readonly critical?: boolean;
  /** Combined item/ability multiplier hook (default 1). */
  readonly otherMultiplier?: number;
}

/** Crit-stage → denominator (Gen 7+ odds): 1/24, 1/8, 1/2, then guaranteed. */
export const CRIT_STAGE_DENOMS = [24, 8, 2] as const;

/**
 * Deterministic critical-hit roll. Stage ≥ 3 is guaranteed (consumes no roll);
 * otherwise crits when `rng.int(denom) === 0`.
 */
export function rollCritical(rng: Rng, critStage = 0): boolean {
  const stage = Math.max(0, Math.floor(critStage));
  if (stage >= CRIT_STAGE_DENOMS.length) return true;
  return rng.int(CRIT_STAGE_DENOMS[stage]!) === 0;
}

/** The standard combat stat-stage multiplier for atk/def/spatk/spdef. */
function stageMultiplier(stage: number): number {
  const s = Math.max(-6, Math.min(6, stage));
  return s >= 0 ? (2 + s) / 2 : 2 / (2 - s);
}

function offensiveKey(category: MoveData['category']): StatKey {
  return category === 'special' ? 'spatk' : 'atk';
}

function defensiveKey(category: MoveData['category']): StatKey {
  return category === 'special' ? 'spdef' : 'def';
}

function stageOf(participant: BattleParticipant, stat: StatKey): number {
  const v = participant.statStages[stat];
  return typeof v === 'number' ? v : 0;
}

function asDefenderTypes(
  types: readonly PokemonType[],
): readonly [PokemonType] | readonly [PokemonType, PokemonType] {
  if (types.length >= 2) return [types[0]!, types[1]!];
  return [types[0]!];
}

/**
 * Compute damage for one single-target move use. Returns an exact integer:
 * 0 when immune / status move / 0-power move, else ≥ 1.
 */
export function computeDamage(
  attacker: BattleParticipant,
  defender: BattleParticipant,
  move: MoveData,
  ctx: DamageContext,
  rng: Rng,
): number {
  if (move.category === 'status' || move.power == null || move.power <= 0) {
    return 0;
  }

  // Type effectiveness first: an immune target deals exactly 0 and consumes no roll.
  const effectiveness = effectivenessAgainst(move.type, asDefenderTypes(ctx.defenderTypes));
  if (effectiveness === 0) {
    return 0;
  }

  const crit = ctx.critical === true;

  // A/D with stat stages; a crit ignores the attacker's negative and the
  // defender's positive stage multipliers.
  const atkStage = stageOf(attacker, offensiveKey(move.category));
  const defStage = stageOf(defender, defensiveKey(move.category));
  const atkMult = crit && atkStage < 0 ? 1 : stageMultiplier(atkStage);
  const defMult = crit && defStage > 0 ? 1 : stageMultiplier(defStage);

  const rawA = ctx.attackerStats[offensiveKey(move.category)];
  const rawD = ctx.defenderStats[defensiveKey(move.category)];
  const A = Math.max(1, Math.floor(rawA * atkMult));
  const D = Math.max(1, Math.floor(rawD * defMult));

  const level = attacker.instance.level;
  const levelTerm = Math.floor((2 * level) / 5 + 2);
  const inner = Math.floor((levelTerm * move.power * A) / D);
  let damage = Math.floor(inner / 50) + 2;

  // Modifiers, each an integer-truncating step in mainline order.
  const targets = ctx.targets ?? 1;
  if (targets > 1) {
    damage = Math.floor(damage * 0.75);
  }

  if (crit) {
    damage = Math.floor(damage * 1.5);
  }

  const r = rng.int(16) + 85; // r ∈ [85, 100]
  damage = Math.floor((damage * r) / 100);

  const stab = ctx.attackerTypes.includes(move.type) ? 1.5 : 1;
  if (stab !== 1) {
    damage = Math.floor(damage * stab);
  }

  damage = Math.floor(damage * effectiveness);

  const burned = move.category === 'physical' && attacker.instance.status === 'burn';
  if (burned) {
    damage = Math.floor(damage * 0.5);
  }

  const other = ctx.otherMultiplier ?? 1;
  if (other !== 1) {
    damage = Math.floor(damage * other);
  }

  // Never-immune moves always do at least 1.
  return Math.max(1, damage);
}
